/**
 * Client for the matching service (matching_backend, see MATCHING_SERVICE_PLAN.md).
 * Same JWT as the Django API (localStorage "access"); base URL from
 * REACT_APP_MATCH_URL (nginx proxies /m/ → matching service :3000).
 */

const MATCHING_BASE_URL =
  process.env.REACT_APP_MATCH_URL || "https://app.itruck.io/m";

const mrequest = async (method, path, body = null) => {
  const response = await fetch(`${MATCHING_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `JWT ${window.localStorage.getItem("access")}`,
    },
    body: body ? JSON.stringify(body) : null,
  });

  const isJson = (response.headers.get("content-type") || "").includes(
    "application/json",
  );

  if (!response.ok) {
    let errorData = { error: `HTTP ${response.status}` };
    if (isJson) {
      try {
        errorData = await response.json();
      } catch {
        /* keep default */
      }
    }
    const err = new Error(
      typeof errorData?.error === "string"
        ? errorData.error
        : `HTTP ${response.status}`,
    );
    err.status = response.status;
    err.data = errorData;
    throw err;
  }

  return isJson ? response.json() : null;
};

/** Live truck supply (availability list) for the /transports browse page. */
export const fetchTrucks = () => mrequest("GET", "/trucks");

/** My own live availability rows (driver / company / admin). */
export const fetchMyTrucks = () => mrequest("GET", "/truck");

/** Incoming requests for my trucks: matched loads with score + my decision. */
export const fetchMyRequests = () => mrequest("GET", "/truck/matches");

/** «Принять запрос» — agree to the load's stated rate (ТЗ §8). */
export const respondMatch = (matchId) =>
  mrequest("POST", `/matches/${matchId}/respond`);

/** «Предложить цену» — own rate for the load. */
export const counterMatch = (matchId, amount, currency) =>
  mrequest("POST", `/matches/${matchId}/counter-offer`, {
    amount,
    ...(currency != null ? { currency } : {}),
  });

/** «Не интересно» — decline the request. */
export const declineMatch = (matchId, reason) =>
  mrequest("POST", `/matches/${matchId}/reject`, reason ? { reason } : {});

/** Post-publish market view: matched count, fresh count, avg score, top trucks. */
export const fetchMarket = (orderId) => mrequest("GET", `/orders/${orderId}/market`);

/** Offers for the load: carrier, truck, distance, rate, response time, status. */
export const fetchOffers = (orderId) => mrequest("GET", `/orders/${orderId}/offers`);

/** «Отправить заявку подходящим машинам» — push to matched trucks.
 * payload: { min_score?: number, match_ids?: string[], vehicle_ids?: number[] } — with match_ids /
 * vehicle_ids only the selected trucks are notified (threshold does not apply; vehicle_ids also
 * picks trucks the engine rejected). */
export const sendToTrucks = (orderId, payload = {}) =>
  mrequest("POST", `/orders/${orderId}/send`, payload);

/** «Откликнуться» with one or several own trucks (matched or not): { vehicle_ids, amount?, currency? }.
 * Without `amount` the load's stated rate is used. Returns { offers, failed: [{vehicle_id, error}] }. */
export const proposeTrucks = (loadId, payload) => mrequest("POST", `/loads/${loadId}/propose`, payload);

/** My trips (carrier: my trucks; dispatcher/client: my loads) with load, vehicle and both parties' contacts. */
export const fetchTrips = (query = "") => mrequest("GET", `/trips${query}`);

/** Publish/refresh truck availability («Обновить состояние», spec §7). */
export const setTruckAvailable = (payload) =>
  mrequest("POST", "/truck/available", payload);

/** Fleet bulk status update (spec §8). */
export const bulkStatus = (vehicleIds, status) =>
  mrequest("POST", "/truck/bulk-status", { vehicle_ids: vehicleIds, status });

/** Fleet bulk freshness extend (spec §8). */
export const bulkConfirm = (vehicleIds) =>
  mrequest("POST", "/truck/bulk-confirm", { vehicle_ids: vehicleIds });

/** «Груз ещё актуален» — restart the load TTL (ТЗ §7.2). */
export const extendLoad = (orderId) => mrequest("POST", `/orders/${orderId}/extend`);

/** Carrier opened a request — feeds the «просмотрено» counter. */
export const viewMatch = (matchId) => mrequest("POST", `/matches/${matchId}/view`);

/** MVP metrics for the home counters (AC-10). */
export const fetchMetrics = () => mrequest("GET", "/orders/metrics/all");

/** Accept an offer → Offer ACCEPTED + Trip + load closed via Django API. */
export const acceptOffer = (orderId, offerId) =>
  mrequest("POST", `/orders/${orderId}/accept`, { offer_id: offerId });

/** Analytics (scope by role: platform / shipper / carrier). days: 1..180, default 30. */
export const fetchAnalytics = (days = 30) =>
  mrequest("GET", `/analytics?days=${encodeURIComponent(days)}`);

/** Runtime matching settings (camelCase: loadTtlHours, truckTtlHours, ...). */
export const fetchMatchSettings = () => mrequest("GET", "/orders/settings");

/** «Отказать» — the load owner turns down one OPEN offer (the load stays open for the others). */
export const rejectOffer = (offerId) => mrequest("POST", `/offers/${offerId}/reject`);

/** «Показать контакты» — reveal the carrier's name + phone for an offer (audited; 403 when not the load owner). */
export const revealContact = (offerId) => mrequest("POST", `/offers/${offerId}/contact`);
